import { destroy, get, patch, post } from 'config/api/config';

const RESPONSE_ROOMMATES_ENDPOINT = '/viewer/responses';

export interface RoommateMembershipHistory {
  id: number;
  roommateGroupId: number;
  firstName: string;
  lastName: string;
  email: string;
  invitationStatus: 'deleted' | 'rejected';
}

export interface RoommateGroupMember extends Omit<RoommateMembershipHistory, 'invitationStatus'> {
  invitationStatus: 'accepted' | 'sent' | 'pending' | 'deleted' | 'rejected';
  leaseId?: number | null;
  applicationId?: number | null;
  invitationSent: boolean;
  isGroupCreator: boolean;
  invitedBy?: string;
}

export const getRoommateGroupMembers = async (
  responseId: string,
): Promise<RoommateGroupMember[]> => {
  const response = await get(`${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommate-group-members`);
  return response.data;
};

export const getRoommateMembershipHistory = async (
  responseId: string,
): Promise<RoommateMembershipHistory[]> => {
  const response = await get(
    `${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommate-membership-history`,
  );
  return response.data;
};

export interface CreateRoommateRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface StudentRoommate {
  id: number;
  roommate_group_id: number;
  email_address: string;
  applicant_id?: number;
  application_id: number | null;
  lease_id: number | null;
  is_invitation_email_sent?: boolean;
  alignmentPending?: boolean;
  name_first?: string;
  name_last?: string;
}

export interface IncomingRoommateInvitation {
  id: number;
  roommateGroupId: number;
  leaseId: number;
  inviterEmail?: string;
  firstName?: string;
  lastName?: string;
  memberFirstName?: string;
  memberLastName?: string;
  invitationStatus?: 'accepted';
  memberEmail?: string;
  applicationId?: number | null;
}

export const createRoommate = async (
  responseId: string,
  request: CreateRoommateRequest,
): Promise<StudentRoommate> => {
  const response = await post(`${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommates`, request);
  return response.data;
};

export const deleteRoommateGroup = async (
  responseId: string,
  roommateGroupId: number,
  removalReason: string,
): Promise<void> => {
  await destroy(`${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommate-groups/${roommateGroupId}`, {
    data: { removalReason },
  });
};

export const sendRoommateInvitation = async (
  responseId: string,
  roommateId: number,
  roommateGroupId: number,
  roommateLeaseId?: number | null,
  roommateApplicationId?: number | null,
): Promise<StudentRoommate> => {
  const response = await patch(
    `${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommates/${roommateId}/invitation`,
    {
      roommateGroupId,
      ...(roommateLeaseId != null ? { roommateLeaseId } : {}),
      ...(roommateApplicationId != null ? { roommateApplicationId } : {}),
    },
  );
  return response.data;
};

export const getRoommateInvitationStatus = async (
  responseId: string,
  roommateId: number,
  roommateGroupId: number,
  roommateLeaseId?: number | null,
  roommateApplicationId?: number | null,
): Promise<{
  invitationSent: boolean;
  invitationStatus?: 'pending' | 'sent' | 'accepted' | 'rejected' | 'removed';
  memberFirstName?: string;
  memberLastName?: string;
}> => {
  const response = await get(
    `${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommates/${roommateId}/invitation-status`,
    {
      params: {
        roommateGroupId,
        ...(roommateLeaseId != null ? { roommateLeaseId } : {}),
        ...(roommateApplicationId != null ? { roommateApplicationId } : {}),
      },
    },
  );
  return response.data;
};

export const getIncomingRoommateInvitations = async (
  responseId: string,
): Promise<IncomingRoommateInvitation[]> => {
  const response = await get(`${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommate-invitations`, {
    params: { includeAccepted: true },
  });
  return response.data;
};

export const confirmIncomingRoommateInvitation = async (
  responseId: string,
  invitation: IncomingRoommateInvitation,
): Promise<StudentRoommate> => {
  const response = await patch(
    `${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommates/${invitation.id}/confirmation`,
    {
      roommateGroupId: invitation.roommateGroupId,
      roommateLeaseId: invitation.leaseId,
    },
  );
  return response.data;
};

export const alignRoommateApplication = async (
  responseId: string,
  roommate: IncomingRoommateInvitation,
): Promise<void> => {
  await patch(`${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommates/${roommate.id}/alignment`, {
    roommateGroupId: roommate.roommateGroupId,
    roommateLeaseId: roommate.leaseId,
  });
};

export const rejectIncomingRoommateInvitation = async (
  responseId: string,
  invitation: IncomingRoommateInvitation,
): Promise<StudentRoommate> => {
  const response = await patch(
    `${RESPONSE_ROOMMATES_ENDPOINT}/${responseId}/roommates/${invitation.id}/rejection`,
    {
      roommateGroupId: invitation.roommateGroupId,
      roommateLeaseId: invitation.leaseId,
    },
  );
  return response.data;
};
